/**
 * Client-SDK-specific specialization of the chat audit event schema +
 * typed refs via `createConverter`.
 *
 * The server-timestamp sentinel is threaded **explicitly** through
 * {@link writeChatAuditEvent} (not hidden inside `augmentOnWrite`) so
 * callers show the sentinel in their code alongside the mutation it's
 * auditing — e.g., a `message_sent` audit call sits next to the message
 * doc's own `createdAt: serverTimestamp()`, both visibly server-stamped.
 */
import {
  addDoc,
  collection,
  getFirestore,
  type CollectionReference,
  type FieldValue,
  type Firestore,
} from 'firebase/firestore';
import {z} from 'zod';
import {
  ClientAuditEventSchema as ClientAuditEventBaseSchema,
  getChatAuditCollectionPath,
  type ClientAuditEvent,
} from '../documents/chatAudit';
import {ClientTimestampSchema, createConverter} from './sdk';

/** Stored audit event view for readers (client-SDK `Timestamp` on read). */
export const StoredAuditEventSchema = ClientAuditEventBaseSchema.extend({
  serverTimestamp: ClientTimestampSchema,
}).loose();

/** Inferred type (client-SDK timestamps). */
export type StoredAuditEvent = z.infer<typeof StoredAuditEventSchema>;

const chatAuditConverter = createConverter({
  schema: StoredAuditEventSchema,
  options: {
    // Read-side validation guarantees consumers see well-formed, Timestamp-
    // typed events. Skip write-side validation because the payload carries a
    // `serverTimestamp()` sentinel (FieldValue), not a resolved Timestamp.
    validateOnRead: true,
    validateOnWrite: false,
  },
});

/**
 * Typed reference for the top-level audit collection. Admin read tooling
 * should use this — subscriptions get validated `StoredAuditEvent`s.
 *
 * @param args.firestore - Firestore instance. Defaults to `getFirestore()`.
 * @returns A `CollectionReference<StoredAuditEvent>`.
 */
export function getChatAuditCollectionRef({
  firestore = getFirestore(),
}: {firestore?: Firestore} = {}): CollectionReference<StoredAuditEvent> {
  return collection(firestore, getChatAuditCollectionPath()).withConverter(
    chatAuditConverter,
  );
}

/**
 * Writes a prepared {@link ClientAuditEvent} to `chatAudit` via the typed
 * collection ref. The caller supplies the `serverNow` sentinel explicitly
 * — no hidden augmentation — so every call site shows its server-time
 * attribution plainly in code.
 *
 * Throws on Firestore errors; callers decide whether to swallow so audit
 * failures don't break chat UX.
 *
 * @param args.event - The client-prepared event.
 * @param args.serverNow - The `serverTimestamp()` sentinel from
 *   `firebase/firestore`. Resolves to the server's write time.
 * @param args.firestore - Firestore instance. Defaults to `getFirestore()`.
 */
export async function writeChatAuditEvent({
  event,
  serverNow,
  firestore = getFirestore(),
}: {
  event: ClientAuditEvent;
  serverNow: FieldValue;
  firestore?: Firestore;
}): Promise<void> {
  const ref = getChatAuditCollectionRef({firestore});
  await addDoc(ref, {...event, serverTimestamp: serverNow});
}
